import { useCallback, useEffect, useRef } from 'react';
import type { AiTutorFormativeFeedback, SubmissionEvaluationResult } from '@/common/api/models';
import { useSSEStream } from '@/common/hooks/useSSEStream';
import {
  useTaskSubmissionActions,
  type EvaluationStreamProgress,
} from '@/student/task/stores/TaskSubmissionStore';

type StreamMessage = {
  type: string;
  [key: string]: unknown;
};

type ServerCloseData = {
  evaluation?: SubmissionEvaluationResult;
  formativeFeedback?: AiTutorFormativeFeedback | null;
  isReadyToProgress?: boolean;
  runId?: string | null;
};

// Full server_close envelope as useSSEStream delivers it
type ServerClosePayload = { valid: boolean; data: ServerCloseData; error?: string; message?: string };

export type EvaluationStreamCompletePayload = {
  evaluation: SubmissionEvaluationResult;
  formativeFeedback: AiTutorFormativeFeedback | null;
  isReadyToProgress: boolean;
  runId: string | null;
  message?: string;
};

type UseEvaluationStreamOptions = {
  onComplete?: (payload: EvaluationStreamCompletePayload) => void;
  onError?: (message: string) => void;
  /**
   * Hard upper bound on stream duration in ms. When elapsed, the stream is
   * aborted, store flags are cleared, and `onError` is invoked with a
   * user-facing timeout message.
   * Pass `undefined` (or omit) to disable the timeout entirely.
   *
   * Preview opts in explicitly via its own constant; the student task controller
   * passes `STUDENT_EVALUATION_TIMEOUT_MS` (250s).
   */
  timeoutMs?: number;
};

const TIMEOUT_ERROR_MESSAGE = 'Evaluation timed out. Please try again.';
const DEFAULT_STREAM_ERROR_MESSAGE = 'Evaluation failed. Please try again.';

export const useEvaluationStream = (
  { onComplete, onError, timeoutMs }: UseEvaluationStreamOptions = {},
) => {
  const {
    setEvaluation,
    setEvaluationDim,
    setExpectedDims,
    setEvaluationProgress,
    clearEvaluationStreamUi,
    clearEvaluationError,
    failEvaluation,
    abortEvaluation,
    setEvaluating,
    setFormativeFeedback,
    setFormativeFeedbackPartial,
  } = useTaskSubmissionActions();

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const timeoutMsRef = useRef(timeoutMs);
  const timeoutHandleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;
  timeoutMsRef.current = timeoutMs;

  const clearTimeoutHandle = useCallback(() => {
    if (timeoutHandleRef.current !== null) {
      clearTimeout(timeoutHandleRef.current);
      timeoutHandleRef.current = null;
    }
  }, []);

  const reportFailure = useCallback((message: string) => {
    failEvaluation(message);
    onErrorRef.current?.(message);
  }, [failEvaluation]);

  const handleMessage = useCallback((data: StreamMessage) => {
    const eventType = data.type;
    if (eventType === 'metadata') {
      const expectedDims = (data.expectedDims as string[]) || [];
      setExpectedDims(expectedDims);
      return;
    }
    if (eventType === 'progress') {
      const progress: EvaluationStreamProgress = {
        stage: data.stage as EvaluationStreamProgress['stage'],
        dimsDone: Number(data.dimsDone ?? 0),
        dimsTotal: Number(data.dimsTotal ?? 0),
        elapsedMs: Number(data.elapsedMs ?? 0),
      };
      setEvaluationProgress(progress);
      return;
    }
    if (eventType === 'dim' && typeof data.name === 'string' && typeof data.level === 'string') {
      setEvaluationDim(data.name, data.level);
      return;
    }
    if (eventType === 'evaluation' && data.evaluation) {
      setEvaluation(data.evaluation as SubmissionEvaluationResult, undefined);
      return;
    }
    if (eventType === 'feedback_delta') {
      setFormativeFeedbackPartial({
        observation: (data.observation as string | null) ?? undefined,
        nextStep: (data.nextStep as string | null) ?? undefined,
      });
    }
  }, [
    setEvaluation,
    setEvaluationDim,
    setExpectedDims,
    setEvaluationProgress,
    setFormativeFeedbackPartial,
  ]);

  const handleServerClose = useCallback((final: ServerClosePayload) => {
    clearTimeoutHandle();
    if (!final.valid || !final.data?.evaluation) {
      const errMsg = final.error || DEFAULT_STREAM_ERROR_MESSAGE;
      reportFailure(errMsg);
      return;
    }
    const { data } = final;
    const evalData = (data.evaluation || {}) as SubmissionEvaluationResult;
    clearEvaluationError();
    setEvaluationProgress(null);
    setEvaluation(evalData, Date.now());
    setFormativeFeedback(data.formativeFeedback ?? null, data.runId ?? null);
    setEvaluating(false);
    clearEvaluationStreamUi();
    onCompleteRef.current?.({
      evaluation: evalData,
      formativeFeedback: data.formativeFeedback ?? null,
      isReadyToProgress: data.isReadyToProgress ?? data.formativeFeedback?.isReadyToProgress ?? false,
      runId: data.runId ?? null,
      message: final.message,
    });
  }, [
    clearEvaluationError,
    clearEvaluationStreamUi,
    clearTimeoutHandle,
    reportFailure,
    setEvaluation,
    setEvaluating,
    setEvaluationProgress,
    setFormativeFeedback,
  ]);

  const handleStreamError = useCallback(() => {
    clearTimeoutHandle();
    reportFailure(DEFAULT_STREAM_ERROR_MESSAGE);
  }, [clearTimeoutHandle, reportFailure]);

  const {
    isStreaming, startStream, stopStream, error,
  } = useSSEStream<StreamMessage, ServerCloseData>({
    source: '',
    method: 'POST',
    onMessage: handleMessage,
    onServerClose: handleServerClose,
    onError: handleStreamError,
  });

  const startEvaluation = useCallback(
    (url: string, body?: unknown) => {
      clearTimeoutHandle();
      clearEvaluationError();
      clearEvaluationStreamUi();
      setEvaluation({} as SubmissionEvaluationResult, undefined);
      setFormativeFeedback(null);
      setEvaluating(true);
      startStream(url, body);
      const timeout = timeoutMsRef.current;
      if (typeof timeout === 'number' && timeout > 0) {
        timeoutHandleRef.current = setTimeout(() => {
          timeoutHandleRef.current = null;
          stopStream();
          reportFailure(TIMEOUT_ERROR_MESSAGE);
        }, timeout);
      }
    },
    [
      clearEvaluationError,
      clearEvaluationStreamUi,
      clearTimeoutHandle,
      reportFailure,
      setEvaluation,
      setEvaluating,
      setFormativeFeedback,
      startStream,
      stopStream,
    ],
  );

  const stopEvaluation = useCallback(() => {
    clearTimeoutHandle();
    stopStream();
    abortEvaluation();
  }, [abortEvaluation, clearTimeoutHandle, stopStream]);

  useEffect(() => () => {
    if (timeoutHandleRef.current !== null) {
      clearTimeout(timeoutHandleRef.current);
      timeoutHandleRef.current = null;
    }
  }, []);

  return {
    isStreaming,
    error,
    startEvaluation,
    stopEvaluation,
  };
};
